import type { FromStore, Project } from '@cartwhl/common/types';
import { ProjectType } from '@cartwhl/common/types';
import type { WizardDefinition } from '@cartwhl/ui/wizard';

export const CANVAS_ON_OPEN_WIZARD_ID = 'canvas-on-open';
export const CANVAS_ON_MOTION_WIZARD_ID = 'canvas-on-motion';

/** Values stored in `UserInfo.wizardsStarted` when a tour has been started. */
export const CANVAS_WIZARD_STARTED_KEYS = {
  onOpen: 'CANVAS_ON_OPEN',
  onMotion: 'CANVAS_ON_MOTION',
} as const;

export const CANVAS_WIZARD_USER_STARTED_KEY_BY_ID: Record<string, string> = {
  [CANVAS_ON_OPEN_WIZARD_ID]: CANVAS_WIZARD_STARTED_KEYS.onOpen,
  [CANVAS_ON_MOTION_WIZARD_ID]: CANVAS_WIZARD_STARTED_KEYS.onMotion,
};

export const CANVAS_ON_OPEN_WIZARD_TARGET_IDS = {
  addMotion: 'canvas-add-motion',
  character: 'canvas-character',
} as const;

export const CANVAS_ON_MOTION_WIZARD_TARGET_IDS = {
  timeline: 'canvas-timeline',
  timelineLastClipMenu: 'canvas-timeline-last-clip-menu',
  download: 'canvas-download',
} as const;

const PRODUCT_FOCUS_LABELS: Record<string, string> = {
  UNREAL: 'Unreal',
  MAYA: 'Maya',
  BLENDER: 'Blender',
  UNITY: 'Unity',
};

export function formatCanvasExportDestination(productFocus?: string): string {
  if (!productFocus) {
    return 'your favorite app';
  }

  return PRODUCT_FOCUS_LABELS[productFocus] ?? 'your favorite app';
}

/** Whether the loaded project already has motion on the timeline (sync from project API). */
export function motionProjectHasTimelineClips(
  project: FromStore<Project> | Project | undefined,
): boolean {
  if (!project || project.type !== ProjectType.MOTION) {
    return false;
  }

  const characterId = Object.keys(project.characters)[0];
  if (!characterId) {
    return false;
  }

  return (project.characters[characterId]?.timeline ?? []).some((item) =>
    Boolean(item.bvhURL),
  );
}

export const canvasOnOpenWizard: WizardDefinition = {
  id: CANVAS_ON_OPEN_WIZARD_ID,
  steps: [
    {
      id: 'ADDING_MOTION',
      targetId: CANVAS_ON_OPEN_WIZARD_TARGET_IDS.addMotion,
      title: 'Adding motion',
      body: 'Bring in motion via video capture, generation, or the Cartwheel Motion Library.',
      placement: 'top',
    },
    {
      id: 'CHARACTERS',
      targetId: CANVAS_ON_OPEN_WIZARD_TARGET_IDS.character,
      title: 'Characters',
      body: 'Generate or swap characters',
      placement: 'left',
    },
  ],
};

export function getCanvasOnMotionWizard(
  exportDestination: string,
): WizardDefinition {
  return {
    id: CANVAS_ON_MOTION_WIZARD_ID,
    steps: [
      {
        id: 'EDITING_MOTION',
        targetId: CANVAS_ON_MOTION_WIZARD_TARGET_IDS.timeline,
        pointerAlignTargetId:
          CANVAS_ON_MOTION_WIZARD_TARGET_IDS.timelineLastClipMenu,
        title: 'Editing motion',
        body: 'Edit and trim motions in the timeline by expanding the clip menu',
        placement: 'top',
        pointerAnchor: 'bottom-right',
        emphasizePointerAlignTarget: true,
      },
      {
        id: 'DOWNLOADING_MOTION',
        targetId: CANVAS_ON_MOTION_WIZARD_TARGET_IDS.download,
        title: 'Downloading motion',
        body: `Like your motion? Send it back to ${exportDestination}.`,
        placement: 'bottom',
        pointerAnchor: 'top-right',
        pointerAlignTargetId: CANVAS_ON_MOTION_WIZARD_TARGET_IDS.download,
        spotlightInset: 1,
      },
    ],
  };
}
